import { useEffect, useMemo, useState } from 'preact/hooks';
import { slowStock, stockRows, stockValue } from '@khata/core';
import { Chips, Empty, PageHead, Row, ShowMore, TextInput } from '../components/index.js';
import { dShort, num, qty, taka } from '../lib/format.js';
import { count, t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { usePaged } from '../lib/paging.js';
import { openSheet } from '../lib/ui.js';

type Filter = 'all' | 'low' | 'dead';
const DEAD_DAYS = 180;

export function Stock(props: { filter?: 'low' | 'dead' }) {
  const s = state.value;
  const m = master.value;
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [filter, setFilter] = useState<Filter>(props.filter ?? 'all');
  useEffect(() => setFilter(props.filter ?? 'all'), [props.filter]);
  const rows = useMemo(() => stockRows(s, m), [s, m]);
  const dead = useMemo(
    () => (filter === 'dead' ? slowStock(s, m, entries.value, today.value, DEAD_DAYS) : null),
    [filter, s, m],
  );
  const lastSale = useMemo(
    () => new Map(dead?.rows.map((r) => [r.item.id, r.lastSale]) ?? []),
    [dead],
  );
  const cats = useMemo(
    () =>
      ['All', ...new Set(rows.map((r) => r.item.category))].sort((a, b) =>
        a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b),
      ),
    [rows],
  );
  const ql = q.trim().toLowerCase();
  const deadIds = dead ? new Set(dead.rows.map((r) => r.item.id)) : null;
  const shown = rows.filter(
    (r) =>
      (cat === 'All' || r.item.category === cat) &&
      (filter === 'all' || (filter === 'low' ? r.low : deadIds!.has(r.item.id))) &&
      (!ql || `${r.item.name} ${r.item.category} ${r.item.origin}`.toLowerCase().includes(ql)),
  );
  const paged = usePaged(shown, 100);
  const sub =
    filter === 'dead' && dead
      ? t('stock_deadSub', { n: num(dead.count), value: taka(dead.value), days: num(DEAD_DAYS) })
      : t('stock_sub', { n: count(rows.length, 'item', 'items'), value: taka(stockValue(s)) });

  return (
    <>
      <PageHead title={t('stock_title')} sub={sub}>
        <button type="button" class="btn btn-primary" onClick={() => openSheet({ kind: 'item' })}>
          {t('stock_add')}
        </button>
      </PageHead>
      <div class="search">
        <TextInput
          value={q}
          onInput={setQ}
          placeholder={t('stock_search')}
          type="search"
          data-testid="stock-search"
        />
      </div>
      <Chips
        options={[
          { key: 'all', label: t('stock_filterAll') },
          { key: 'low', label: t('stock_filterLow') },
          { key: 'dead', label: t('stock_filterDead') },
        ]}
        value={filter}
        onChange={setFilter}
      />
      <Chips
        options={cats.map((c) => ({ key: c, label: c === 'All' ? t('stock_all') : c }))}
        value={cat}
        onChange={setCat}
      />
      <div class="list" data-testid="stock-list">
        {shown.length === 0 && (
          <Empty>{rows.length === 0 ? t('stock_empty') : t('stock_noMatch')}</Empty>
        )}
        {paged.visible.map((r) => (
          <Row
            key={r.item.id}
            title={r.item.name}
            sub={
              filter === 'dead'
                ? `${r.item.category} · ${lastSale.get(r.item.id) ? t('reports_lastSold', { date: dShort(lastSale.get(r.item.id)!) }) : t('reports_neverSoldShort')} · ${t('stock_buy')} ${taka(r.avgCost)}`
                : `${r.item.category}${r.item.origin ? ' · ' + r.item.origin : ''} · ${t('stock_buy')} ${taka(r.avgCost)} · ${t('stock_sell')} ${taka(r.item.sellPrice)}${r.defQty > 0 ? ` · ${qty(r.defQty)} ${t('stock_defective')}` : ''}`
            }
            amount={`${qty(r.qty)} ${r.item.unit}`}
            amountTone={r.low ? 'bad' : 'ink'}
            amountSub={
              filter === 'dead'
                ? taka(r.value)
                : r.out
                  ? t('stock_out')
                  : r.low
                    ? t('stock_low')
                    : ''
            }
            amountSubTone={r.low && filter !== 'dead' ? 'bad' : 'muted'}
            onClick={() => openSheet({ kind: 'item', id: r.item.id })}
          />
        ))}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
